/**
 * MODULE: platform/browser/frontend/hooks/useBrowserCommandInput.ts
 * PURPOSE: Shared command input state for the chat panel and collapsed command dock.
 * DEPENDENCIES: browser.types ChatMessage, BrowserSession
 * PUBLIC_API: useBrowserCommandInput, BrowserCommandInput
 * SIDE_EFFECTS: Invokes onMessage when user sends
 * AGENT_SAFETY: 🟡 RESTRICTED
 * RECENT: 2026-06-17 [2.2.1414] — Shared input for panel + BrowserCommandDock.
 */

import { useMemo, useRef, useState } from 'react';
import type { BrowserSession, ChatMessage } from '../types/browser.types';

export function useBrowserCommandInput(options: {
  session: BrowserSession | null;
  isStreaming: boolean;
  messages: ChatMessage[];
  onMessage: (text: string) => void;
}) {
  const { session, isStreaming, messages, onMessage } = options;
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isDisabled = !session || isStreaming;

  const inputPlaceholder = useMemo(() => {
    const last = messages[messages.length - 1];
    if (last?.role === 'assistant' && /action limit/i.test(last.content)) {
      return 'Follow up to continue — e.g. "Now extract the leadership team from this page"';
    }
    if (
      last?.role === 'assistant' &&
      /stopped —|did not progress|404|not found/i.test(last.content)
    ) {
      return 'Try a narrower follow-up — e.g. "Go to brazn.com directly and summarize the homepage"';
    }
    return 'e.g. Find Acme Corp on LinkedIn and summarize their latest posts';
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isDisabled) return;
    onMessage(text);
    setInput('');
  };

  return {
    input,
    setInput,
    inputRef,
    inputPlaceholder,
    handleSend,
    isDisabled,
  };
}

export type BrowserCommandInput = ReturnType<typeof useBrowserCommandInput>;
